import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { formCard, pageTitle } from '@/lib/form-styles'
import { api } from '@/lib/axios'
import { ZeilenBlock, ZeilenListe, Feld as Spalte, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock'
import { FeldStatusZeichen, mitStand, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { userTimeSettingsApi } from '../api/time-tracking.api'
import type { MonthlyReportTimeSource, UserWithTimeSettings } from '../types/time-tracking.types'
import { UserCog } from 'lucide-react'

/**
 * Stundensätze, Qualifikation und Stundenquelle je Mitarbeiter.
 *
 * AP-36 Phase C: **Zeilenblock** — jede Zeile ist eine Gruppe und speichert beim
 * Verlassen der Zeile, nur die geänderten Felder, mit dem Stand der Einstellungen
 * dieses Mitarbeiters. Stift, Häkchen und Kreuz je Zeile gibt es nicht mehr.
 */

function displayName(user: UserWithTimeSettings): string {
  const p = user.profile
  if (p?.firstName || p?.lastName) {
    return [p.firstName, p.lastName].filter(Boolean).join(' ')
  }
  return user.email
}

const SPALTEN: SpaltenDef[] = [
  { key: 'name', label: 'Mitarbeiter', spur: 'minmax(12rem,2fr)', min: 190, schmal: 'w-full' },
  { key: 'rolle', label: 'Rolle', spur: 'minmax(8rem,1fr)', min: 120, schmal: 'w-40' },
  { key: 'sk', label: 'SK-Stundensatz (€)', spur: '8rem', min: 110, rechts: true, schmal: 'w-32' },
  { key: 'vk', label: 'VK-Stundensatz (€)', spur: '8rem', min: 110, rechts: true, schmal: 'w-32' },
  { key: 'quali', label: 'Qualifikation', spur: 'minmax(10rem,1fr)', min: 150, schmal: 'w-48' },
  { key: 'quelle', label: 'Stunden Monatsbericht', spur: '12rem', min: 170, schmal: 'w-48' },
  // KA-47: nur mit dem Scan-Baustein — ohne ihn gibt es keine QR-Pflicht.
  { key: 'qr', label: 'QR-Pflicht', spur: '7rem', min: 90, schmal: 'w-28' },
]

export default function UserSettingsPage() {
  const { t } = useTranslation()
  const [users, setUsers] = useState<UserWithTimeSettings[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const mitScan = useAuthStore((s) => s.hasModule('TIME_TRACKING.SCAN'))

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await userTimeSettingsApi.list()
      setUsers(res.data)
    } catch {
      setError(t('userSettings.loadError', { defaultValue: 'Fehler beim Laden der Benutzerdaten.' }))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { load() }, [load])

  const spalten = SPALTEN.filter((s) => s.key !== 'qr' || mitScan).map((s) => ({
    ...s,
    label:
      s.key === 'name' ? t('userSettings.col.name', { defaultValue: s.label })
      : s.key === 'rolle' ? t('userSettings.col.role', { defaultValue: s.label })
      : s.key === 'sk' ? t('userSettings.col.skStundensatz', { defaultValue: s.label })
      : s.key === 'vk' ? t('userSettings.col.vkStundensatz', { defaultValue: s.label })
      : s.key === 'quali' ? t('userSettings.col.qualifikation', { defaultValue: s.label })
      : s.key === 'qr' ? t('userSettings.col.qrRequired', { defaultValue: s.label })
      : t('userSettings.col.monthlyReportTimeSource', { defaultValue: s.label }),
  }))

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <UserCog size={22} className="text-muted-foreground" />
        <h1 className={pageTitle}>{t('userSettings.title', { defaultValue: 'Benutzereinstellungen' })}</h1>
      </div>

      <p className="text-sm text-muted-foreground">
        {t('userSettings.description', { defaultValue: 'Hier pflegst du je Mitarbeiter den Selbstkostenstundensatz, den Verkaufsstundensatz und die Qualifikation.' })}{' '}
        Eine Zeile wird gespeichert, sobald du sie verlässt.
        {mitScan && ' „QR-Pflicht" gilt, wenn in den Grundeinstellungen „QR-Code-Scan erzwingen" an ist; ohne Häkchen stempelt der Mitarbeiter ohne Code.'}
      </p>

      <div className={formCard}>
        {loading && <p className="text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Wird geladen…' })}</p>}
        {error && <p className="text-sm text-destructive">{error}</p>}
        {!loading && !error && users.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('userSettings.noUsers', { defaultValue: 'Keine Benutzer gefunden.' })}</p>
        )}
        {!loading && !error && users.length > 0 && (
          <ZeilenListe spalten={spalten}>
            {users.map((user) => (
              <MitarbeiterZeile key={user.id} user={user} mitScan={mitScan} />
            ))}
          </ZeilenListe>
        )}
      </div>
    </div>
  )
}

function MitarbeiterZeile({ user, mitScan }: { user: UserWithTimeSettings; mitScan: boolean }) {
  const { t } = useTranslation()
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `userTimeSettings:${user.id}`,
    bezeichnung: displayName(user),
    daten: (user.timeSettings as unknown as Datensatz | null) ?? null,
    schreibe: (patch, stand) =>
      api.put<Datensatz>(`/time-config/user-settings/${user.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/time-config/user-settings/${user.id}`, method: 'PUT' },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: displayName(user) })
  // Die Sätze sind Preisfelder (`time.price.view`): Ohne das Recht nimmt der Server
  // sie aus jeder Antwort — ein Feld, das man nicht sehen kann, bietet die Maske nicht an.
  const darfPreise = erfuelltRecht('time.price.view', useAuthStore((s) => s.user?.permissions))
  const satz = { art: 'zahl' as const, stellen: 2, gruppe, gesperrt: !darfPreise, pruefe: (w: number | null) => (w !== null && w < 0 ? 'Nicht negativ.' : null) }
  const sk = useFeld<number | null>(stelle, 'selbstkostenstundensatz', { ...satz, label: 'SK-Stundensatz' })
  const vk = useFeld<number | null>(stelle, 'verkaufsstundensatz', { ...satz, label: 'VK-Stundensatz' })
  const quali = useFeld<string | null>(stelle, 'qualifikation', { gruppe, label: 'Qualifikation' })
  // Leer = Übersteuerung entfernen, also zurück auf den Mandanten-Standard.
  const quelle = useFeld<MonthlyReportTimeSource | null>(stelle, 'monthlyReportTimeSource', {
    art: 'auswahl',
    gruppe,
    label: 'Stunden Monatsbericht',
    lies: (x) => (x === '' ? null : (x as MonthlyReportTimeSource)),
    schreib: (w) => w ?? '',
  })
  // KA-47: Ohne Einstellungszeile gilt die Pflicht (Vorgabe am Server) — das Häkchen zeigt das auch.
  const qr = useFeld<boolean | undefined>(stelle, 'qrRequired', { art: 'schalter', gruppe, label: 'QR-Pflicht' })

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={user.id}>
      <Spalte spalte="name">
        <span className="block truncate font-medium text-foreground">{displayName(user)}</span>
        <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
      </Spalte>
      <Spalte spalte="rolle">
        <span className="text-sm text-muted-foreground">{user.roles.map((r) => r.name).join(', ')}</span>
      </Spalte>
      <Spalte spalte="sk">
        <input aria-label="SK-Stundensatz" inputMode="decimal" placeholder={darfPreise ? '0,00' : 'verborgen'} title={darfPreise ? undefined : 'Nur mit dem Recht „Preisfelder: sehen"'} className={`${zelle} text-right`} {...sk.props} />
      </Spalte>
      <Spalte spalte="vk">
        <input aria-label="VK-Stundensatz" inputMode="decimal" placeholder={darfPreise ? '0,00' : 'verborgen'} title={darfPreise ? undefined : 'Nur mit dem Recht „Preisfelder: sehen"'} className={`${zelle} text-right`} {...vk.props} />
      </Spalte>
      <Spalte spalte="quali">
        <input
          aria-label="Qualifikation"
          placeholder={t('userSettings.qualifikationPlaceholder', { defaultValue: 'z. B. Elektrotechniker' })}
          className={zelle}
          {...quali.props}
        />
      </Spalte>
      <Spalte spalte="quelle">
        <select aria-label="Stunden Monatsbericht" className={zelle} {...quelle.props}>
          <option value="">{t('userSettings.timeSourceDefault', { defaultValue: 'Standard des Mandanten' })}</option>
          <option value="TIME_ENTRIES">{t('userSettings.timeSourceEntries', { defaultValue: 'Aus der Zeiterfassung' })}</option>
          <option value="MANUAL">{t('userSettings.timeSourceManual', { defaultValue: 'Händisch erfasst' })}</option>
        </select>
      </Spalte>
      {mitScan && (
        <Spalte spalte="qr">
          <span className="flex items-center justify-center gap-1">
            <input type="checkbox" aria-label="QR-Pflicht" className="h-4 w-4 accent-[hsl(var(--primary))]" {...qr.schalter} checked={qr.wert ?? true} />
            <FeldStatusZeichen status={qr} />
          </span>
        </Spalte>
      )}
    </ZeilenBlock>
  )
}
